import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_SIZE = { width: 0, height: 0, top: 0, left: 0 };

const getRectForSelector = (selector) => {
  if (!selector) return DEFAULT_SIZE;
  const element = document.querySelector(selector);
  if (!element) return DEFAULT_SIZE;
  const rect = element.getBoundingClientRect();
  return {
    width: rect.width,
    height: rect.height,
    top: rect.top,
    left: rect.left
  };
};

const getBubblePosition = (rect, placement = 'bottom') => {
  const maxWidth = Math.min(window.innerWidth - 32, 360);
  const gap = 18;
  const bubbleHeight = 170;
  const targetCenterX = rect.left + rect.width / 2;
  let resolvedPlacement = placement;
  let top = rect.top + rect.height + gap;
  let left = rect.left + rect.width / 2 - maxWidth / 2;

  if (placement === 'top') {
    top = rect.top - bubbleHeight - gap;
  }
  if (placement === 'right') {
    top = rect.top + rect.height / 2;
    left = rect.left + rect.width + gap;
  }
  if (placement === 'left') {
    top = rect.top + rect.height / 2;
    left = rect.left - maxWidth - gap;
  }

  if (placement === 'bottom' && top + bubbleHeight > window.innerHeight - 16) {
    resolvedPlacement = 'top';
    top = rect.top - bubbleHeight - gap;
  } else if (placement === 'top' && top < 16) {
    resolvedPlacement = 'bottom';
    top = rect.top + rect.height + gap;
  }

  left = Math.max(16, Math.min(left, window.innerWidth - maxWidth - 16));
  top = Math.max(16, Math.min(top, window.innerHeight - bubbleHeight - 16));

  const arrowLeft = Math.max(26, Math.min(targetCenterX - left - 10, maxWidth - 42));

  return { top, left, maxWidth, resolvedPlacement, arrowLeft };
};

export function DashboardDemoOverlay({ modal = null, bubble = null }) {
  const [targetRect, setTargetRect] = useState(DEFAULT_SIZE);

  useEffect(() => {
    if (!bubble?.targetSelector) {
      setTargetRect(DEFAULT_SIZE);
      return;
    }

    const update = () => setTargetRect(getRectForSelector(bubble.targetSelector));

    // rAF-Throttle gegen Jitter auf iOS Safari: während Smooth-Scroll feuert
    // `scroll` 60×/s — wir brauchen max. einmal pro Frame eine Neu-Messung.
    let rafId = null;
    const scheduleUpdate = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    update();
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    window.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleUpdate);
      window.visualViewport.addEventListener('scroll', scheduleUpdate);
    }

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, { capture: true });
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', scheduleUpdate);
        window.visualViewport.removeEventListener('scroll', scheduleUpdate);
      }
    };
  }, [bubble?.targetSelector]);

  useEffect(() => {
    if (!bubble?.allowInteraction || !bubble?.targetSelector) return undefined;
    const element = document.querySelector(bubble.targetSelector);
    if (!element) return undefined;

    const previousPosition = element.style.position;
    const previousZIndex = element.style.zIndex;
    const previousPointerEvents = element.style.pointerEvents;

    if (!element.style.position || element.style.position === 'static') {
      element.style.position = 'relative';
    }
    element.style.zIndex = '100050';
    element.style.pointerEvents = 'auto';

    return () => {
      element.style.position = previousPosition;
      element.style.zIndex = previousZIndex;
      element.style.pointerEvents = previousPointerEvents;
    };
  }, [bubble?.allowInteraction, bubble?.targetSelector]);

  const bubblePosition = useMemo(() => {
    if (!bubble || !bubble.targetSelector) return null;
    return getBubblePosition(targetRect, bubble.placement);
  }, [bubble, targetRect]);

  const shouldLockInteraction = Boolean((modal || bubble) && !modal?.allowInteraction && !bubble?.allowInteraction);

  // Interaktionen sperren: User soll während der Tour nur zusehen,
  // nicht selbst scrollen, wischen oder klicken. Programmatisches
  // `window.scrollTo` bleibt möglich, weil es kein Event auslöst.
  useEffect(() => {
    if (!shouldLockInteraction) return undefined;
    const blockEvent = (e) => {
      e.preventDefault();
    };
    const opts = { passive: false, capture: true };
    window.addEventListener('wheel', blockEvent, opts);
    window.addEventListener('touchmove', blockEvent, opts);
    const prevBodyOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overscrollBehavior = 'none';
    return () => {
      window.removeEventListener('wheel', blockEvent, opts);
      window.removeEventListener('touchmove', blockEvent, opts);
      document.body.style.overscrollBehavior = prevBodyOverscroll;
    };
  }, [shouldLockInteraction]);

  if (!modal && !bubble) return null;

  const blockInteraction = !modal?.allowInteraction && !bubble?.allowInteraction;
  const overlayOpacity = modal ? 0.34 : 0.06;
  const overlayBlur = modal ? 'blur(4px)' : 'none';

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[99980] ${blockInteraction ? 'pointer-events-auto' : 'pointer-events-none'}`}
        style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})`, backdropFilter: overlayBlur }}
      />

      {bubble && bubblePosition && targetRect.width > 0 && (
        <>
          {bubble.allowInteraction && (
            <button
              type="button"
              aria-label={bubble.targetLabel || 'Aktion ausführen'}
              className="fixed z-[100060] bg-transparent border-0 p-0 m-0 cursor-pointer"
              style={{
                top: targetRect.top,
                left: targetRect.left,
                width: targetRect.width,
                height: targetRect.height
              }}
              onClick={() => {
                if (typeof bubble.onTargetClick === 'function') {
                  bubble.onTargetClick();
                  return;
                }
                const element = document.querySelector(bubble.targetSelector);
                if (element && typeof element.click === 'function') {
                  element.click();
                }
              }}
            />
          )}
          <div
            className={`fixed z-[99981] rounded-2xl border-2 border-[#10B981] shadow-[0_0_22px_rgba(16,185,129,0.35)] pointer-events-none ${bubble.clickHint ? 'demo-click-pulse' : ''}`}
            style={{
              top: targetRect.top - 8,
              left: targetRect.left - 8,
              width: targetRect.width + 16,
              height: targetRect.height + 16,
              transformOrigin: 'center center'
            }}
          />
          <div
            className="fixed z-[99982] p-4 rounded-2xl border border-[#10B981]/50 bg-black/95 shadow-2xl shadow-[#10B981]/20 pointer-events-none"
            style={{
              top: bubblePosition.top,
              left: bubblePosition.left,
              width: bubblePosition.maxWidth
            }}
          >
            <div
              className="absolute w-5 h-5 bg-black/95 border-[#10B981]/50 rotate-45"
              style={{
                left: bubblePosition.arrowLeft,
                top: bubblePosition.resolvedPlacement === 'top' ? 'calc(100% - 10px)' : '-10px',
                borderLeftWidth: bubblePosition.resolvedPlacement === 'top' ? '0px' : '1px',
                borderTopWidth: bubblePosition.resolvedPlacement === 'top' ? '0px' : '1px',
                borderRightWidth: bubblePosition.resolvedPlacement === 'top' ? '1px' : '0px',
                borderBottomWidth: bubblePosition.resolvedPlacement === 'top' ? '1px' : '0px'
              }}
            />
            {bubble.eyebrow && (
              <p className="text-[#10B981] text-[11px] font-bold uppercase tracking-[0.18em] mb-2">
                {bubble.eyebrow}
              </p>
            )}
            {bubble.title && (
              <h3 className="text-white font-bold text-base mb-2">{bubble.title}</h3>
            )}
            {bubble.text && (
              <p className="text-white/75 text-sm leading-relaxed">{bubble.text}</p>
            )}
          </div>
        </>
      )}

      {modal && (
        <div className="fixed inset-0 z-[99990] flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-[28px] border border-[#10B981]/35 bg-[rgba(5,5,5,0.98)] backdrop-blur-xl shadow-2xl shadow-[#10B981]/20 overflow-hidden">
            <div className="px-6 py-6 sm:px-8 sm:py-8">
              {modal.eyebrow && (
                <p className="text-[#10B981] text-[11px] font-bold uppercase tracking-[0.22em] mb-3">
                  {modal.eyebrow}
                </p>
              )}
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{modal.title}</h2>
              <p className="text-white/70 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                {modal.text}
              </p>
              {(modal.primaryLabel || modal.secondaryLabel) && (
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  {modal.primaryLabel && (
                    <button
                      onClick={modal.onPrimary}
                      className="flex-1 px-5 py-3 rounded-2xl bg-[#10B981] text-black font-bold hover:bg-[#22c58b] transition-all"
                    >
                      {modal.primaryLabel}
                    </button>
                  )}
                  {modal.secondaryLabel && (
                    <button
                      onClick={modal.onSecondary}
                      className="flex-1 px-5 py-3 rounded-2xl border border-white/15 text-white/80 hover:bg-white/5 transition-all"
                    >
                      {modal.secondaryLabel}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

export function DashboardDemoVideoPlayer({ videoUrl, onClose, onEnded }) {
  const videoRef = useRef(null);
  const [fallbackMs, setFallbackMs] = useState(12000);

  useEffect(() => {
    if (!videoUrl || !videoRef.current) return undefined;

    const video = videoRef.current;
    video.volume = 1;

    // Autoplay-Strategie: Start muted (immer erlaubt), dann sofort nach play
    // unmuten. Zusätzlich try mit Ton direkt, falls der Browser es ohnehin zulässt.
    const startAndUnmute = async () => {
      try {
        video.muted = true;
        await video.play();
      } catch {
        // selbst muted play scheiterte — kein weiterer Versuch
        return;
      }
      // Sobald play gelungen ist, Ton anschalten.
      try {
        video.muted = false;
        video.volume = 1;
      } catch {}
    };
    startAndUnmute();

    return undefined;
  }, [videoUrl]);

  useEffect(() => {
    if (!videoUrl) return undefined;
    const timeoutId = setTimeout(() => {
      if (onEnded) onEnded();
    }, fallbackMs);
    return () => clearTimeout(timeoutId);
  }, [videoUrl, fallbackMs, onEnded]);

  if (!videoUrl) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100000] bg-black flex items-center justify-center">
      <video
        ref={videoRef}
        src={videoUrl}
        autoPlay
        playsInline
        muted
        className="max-w-full max-h-full object-contain pointer-events-none select-none"
        onLoadedMetadata={(event) => {
          const durationSeconds = Number(event.currentTarget.duration);
          if (durationSeconds > 0) {
            setFallbackMs(Math.max(6000, Math.ceil(durationSeconds * 1000) + 1200));
          }
        }}
        onEnded={onEnded}
      />
    </div>,
    document.body
  );
}

function RewardFlyoutChip({ item, index, onDone }) {
  const [style, setStyle] = useState(null);

  useEffect(() => {
    const startTop = window.innerHeight * 0.42 + index * 56;
    const startLeft = window.innerWidth / 2 - 92;
    const targetRect = getRectForSelector(item.targetSelector);
    const endTop = targetRect.top + targetRect.height / 2 - 18;
    const endLeft = targetRect.left + targetRect.width / 2 - 62;

    setStyle({
      top: startTop,
      left: startLeft,
      opacity: 1,
      transform: 'scale(1)',
      transition: 'top 1100ms cubic-bezier(0.22, 1, 0.36, 1), left 1100ms cubic-bezier(0.22, 1, 0.36, 1), opacity 1100ms ease, transform 1100ms ease'
    });

    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setStyle({
          top: endTop,
          left: endLeft,
          opacity: 0.16,
          transform: 'scale(0.55)',
          transition: 'top 1100ms cubic-bezier(0.22, 1, 0.36, 1), left 1100ms cubic-bezier(0.22, 1, 0.36, 1), opacity 1100ms ease, transform 1100ms ease'
        });
      });
    });

    const timeoutId = setTimeout(onDone, 1180);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [index, item, onDone]);

  if (!style) return null;

  return (
    <div
      className="fixed z-[100010] px-4 py-2 rounded-full bg-black/92 border border-[#10B981]/45 text-white text-sm font-bold shadow-2xl shadow-[#10B981]/20 pointer-events-none"
      style={style}
    >
      {item.label}
    </div>
  );
}

export function DashboardRewardFlyouts({ items = [], onComplete = null }) {
  const [visibleItems, setVisibleItems] = useState(items);

  useEffect(() => {
    setVisibleItems(items);
  }, [items]);

  useEffect(() => {
    if (!visibleItems.length && items.length === 0 && onComplete) {
      onComplete();
    }
  }, [items.length, onComplete, visibleItems.length]);

  if (!visibleItems.length) return null;

  return createPortal(
    <>
      {visibleItems.map((item, index) => (
        <RewardFlyoutChip
          key={item.id}
          item={item}
          index={index}
          onDone={() => {
            setVisibleItems((current) => {
              const next = current.filter((entry) => entry.id !== item.id);
              if (next.length === 0 && onComplete) {
                setTimeout(onComplete, 120);
              }
              return next;
            });
          }}
        />
      ))}
    </>,
    document.body
  );
}

export default DashboardDemoOverlay;
